import { prefsOf, type Prefs, type Progress } from '../game/merge.ts';
import type { GearItem } from '../game/gear.ts';
import type { RunProof } from '../game/anticheat.ts';

export interface Account {
  name: string;
  email: string | null; // null for Telegram accounts
  telegram?: string | null; // @username or name, for Telegram accounts
}

// The ?src= tag of the first link this browser arrived by (a channel, the bot, a friend), sent with a sign-up so the
// server can count players per channel. The server validates it.
const SOURCE = ((): string | undefined => {
  try {
    const tag = new URLSearchParams(location.search).get('src');
    if (tag && !localStorage.getItem('mana-storm-src')) localStorage.setItem('mana-storm-src', tag.slice(0, 32));
    return localStorage.getItem('mana-storm-src') ?? undefined;
  } catch {
    return undefined; // no location or storage (tests, private mode)
  }
})();

// The Daily streak (consecutive UTC days with a Daily run) and whether today's is done.
export interface DailyStatus {
  streak: number;
  today: boolean;
}

export interface LeaderRow {
  rank: number;
  name: string;
  kills: number;
  time: number;
  victory?: boolean; // one realm's board
  cleared?: number; // the All board: realms cleared
  xp?: number; // the player's main-level xp
  level: number;
  me?: boolean;
}

export interface Leaderboard {
  stage: string;
  date?: string; // the Daily board's UTC day
  rows: LeaderRow[];
  me: LeaderRow | null;
}

export interface RunSettings {
  stage: string;
  tier: number;
  character: string;
  daily: boolean;
}

// One purchase, enhancement, fusion or claim. The server checks the price and moves the gold.
export type Action =
  | { kind: 'shop'; id: string }
  | { kind: 'chest'; id: string; count: 1 | 10 }
  | { kind: 'enhance' | 'reforge' | 'fuse' | 'salvage' | 'equip'; item: string }
  | { kind: 'salvage-commons' }
  | { kind: 'bounty'; enemy: string };

export interface ActResult {
  progress: Progress;
  gold?: number; // the gold this action moved, negative for a purchase
  items?: GearItem[];
}

export interface RunStart {
  token: string;
  progress: Progress;
}

export interface ScoreResult {
  rank: number;
  improved: boolean;
  daily?: string;
  streak?: number;
  today?: boolean;
  progress?: Progress;
  gold?: number; // the gold this run credited
  loot?: GearItem | null;
}

interface ScoreBody {
  stage: string;
  kills: number;
  time: number;
  victory: boolean;
  level: number;
  tier?: number;
  character?: string;
  daily?: boolean;
  goldRun?: number;
  greed?: number;
  bestiary?: Record<string, number>;
  evolved?: string[];
  proof?: RunProof;
  token?: string | null;
}

type Result<T> = { ok: true; data: T } | { ok: false; error: string };

async function call<T>(method: string, path: string, body?: unknown): Promise<Result<T>> {
  try {
    const res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const text = await res.text();
    const json = text ? (JSON.parse(text) as T & { error?: string }) : null;
    return res.ok ? { ok: true, data: json as T } : { ok: false, error: json?.error ?? 'server' };
  } catch {
    return { ok: false, error: 'network' };
  }
}

// Account session, the account's progression and the leaderboard. Playing requires an account (Game.requireAccount);
// offline, menus still render from the last save but buying and earning need the connection.
// What this device keeps: the prefs it writes, and the codex it held before the server owned progress (handed over).
export type LocalSave = Prefs & Pick<Progress, 'bestiary' | 'bounties' | 'evolutions'> & { dailyClaimed?: string };

export class Cloud {
  account: Account | null = null;
  daily: DailyStatus | null = null;
  private pushTimer = 0;

  private readonly local: () => LocalSave;
  private readonly apply: (progress: Progress) => void;
  private readonly changed: () => void;

  // Plain fields, not parameter properties: tests/cloud.test.ts loads this file with node's type stripping.
  constructor(local: () => LocalSave, apply: (progress: Progress) => void, changed: () => void) {
    this.local = local;
    this.apply = apply;
    this.changed = changed;
  }

  async restore(): Promise<void> {
    const me = await call<{ user: Account | null; daily?: DailyStatus }>('GET', '/api/me');
    if (!me.ok || !me.data.user) return;
    this.account = me.data.user;
    this.daily = me.data.daily ?? null;
    this.changed();
    await this.pull();
  }

  // Sign-in answers carry no streak: ask for it once the account is known.
  private async refreshDaily(): Promise<void> {
    const me = await call<{ daily?: DailyStatus }>('GET', '/api/me');
    if (!me.ok) return;
    this.daily = me.data.daily ?? null;
    this.changed();
  }

  async signIn(mode: 'login' | 'register', email: string, password: string, name: string): Promise<string | null> {
    const res = await call<{ user: Account }>('POST', mode === 'login' ? '/api/login' : '/api/register', mode === 'login' ? { email, password } : { email, password, name, src: SOURCE });
    if (!res.ok) return res.error;
    this.account = res.data.user;
    this.changed();
    await this.pull();
    await this.refreshDaily();
    return null;
  }

  // Telegram sign-in (or, with link, connecting Telegram to the signed-in player): a deep link to the bot,
  // then polling until the player confirms there.
  async telegramStart(link = false): Promise<{ code: string; url: string; match: number } | string> {
    const res = await call<{ code: string; url: string; match: number }>('POST', '/api/telegram/start', { link, src: SOURCE });
    return res.ok ? res.data : res.error;
  }

  async telegramPoll(code: string): Promise<string> {
    const res = await call<{ status: 'pending' | 'done'; user?: Account }>('POST', '/api/telegram/poll', { code });
    if (!res.ok) return res.error;
    if (res.data.status !== 'done' || !res.data.user) return 'pending';
    this.account = res.data.user;
    this.changed();
    await this.pull();
    await this.refreshDaily();
    return 'done';
  }

  // The caller then clears the local save: the progress is the account's, and the next player here must not inherit it.
  async signOut(): Promise<void> {
    window.clearTimeout(this.pushTimer);
    await this.push(); // a pick still waiting on the debounce would otherwise be lost
    await call('POST', '/api/logout', {});
    this.account = null;
    this.daily = null;
    this.changed();
  }

  // Debounced so a burst of selections is one request. Only the mage/realm/tier pick travels.
  schedulePush(): void {
    if (!this.account) return;
    window.clearTimeout(this.pushTimer);
    this.pushTimer = window.setTimeout(() => void this.push(), 1200);
  }

  async act(action: Action): Promise<ActResult | string> {
    if (!this.account) return 'signed_out';
    const res = await call<ActResult>('POST', '/api/act', action);
    if (!res.ok) return res.error;
    this.apply(res.data.progress);
    return res.data;
  }

  // The run's settings are signed into its token, so the run cannot change them when it is submitted.
  async startRun(settings: RunSettings): Promise<RunStart | null> {
    if (!this.account) return null;
    const res = await call<{ token?: string; progress?: Progress }>('POST', '/api/run/start', settings);
    if (!res.ok || typeof res.data?.token !== 'string' || !res.data.progress) return null;
    this.apply(res.data.progress); // the run's permanent mods come from here, not from this device's cache
    return { token: res.data.token, progress: res.data.progress };
  }

  // A check-in while a run is being played: fire-and-forget, the server keeps it for the limits report.
  async beat(token: string, t: number, kills: number, level: number, gold: number): Promise<void> {
    if (!this.account) return;
    await call('POST', '/api/run/beat', { token, t: Math.round(t * 10) / 10, kills, level, gold });
  }

  // daily: the run was today's Daily Trial; it is ranked on the day's board and the answer carries the new streak.
  async submitScore(score: ScoreBody): Promise<ScoreResult | null> {
    if (!this.account) return null;
    const res = await call<ScoreResult>('POST', '/api/score', score);
    if (!res.ok) return null;
    if (res.data.progress) this.apply(res.data.progress);
    if (res.data.daily) {
      this.daily = { streak: res.data.streak ?? 0, today: res.data.today ?? true };
      this.changed();
    }
    return res.data;
  }

  async leaderboard(stage: string): Promise<Leaderboard | null> {
    const res = await call<Leaderboard>('GET', `/api/leaderboard?stage=${encodeURIComponent(stage)}`);
    return res.ok ? res.data : null;
  }

  private async pull(): Promise<void> {
    const res = await call<{ save: (Progress & Prefs) | null; codexOpen?: boolean }>('GET', '/api/save');
    if (!res.ok || !res.data.save) return;
    let save = res.data.save;
    // Before the server's copy replaces it, hand over the codex this device kept while it was never uploaded.
    if (res.data.codexOpen) {
      const { bestiary, bounties, evolutions, dailyClaimed } = this.local();
      const merged = await call<{ progress: Progress }>('POST', '/api/codex', { bestiary, bounties, evolutions, dailyClaimed });
      if (merged.ok) save = { ...save, ...merged.data.progress };
    }
    this.apply(save);
  }

  private async push(): Promise<void> {
    if (!this.account) return;
    await call('PUT', '/api/save', { save: prefsOf(this.local()) });
  }
}
